<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  authoriseWorkPack,
  getWorkPackControl,
  issueWorkPack,
  reviewWorkPackSection,
  updateWorkPackRoles,
  verifyWorkPack,
  WORK_PACK_STATUS_LABEL,
  type WorkPackRoleKey,
  type WorkPackRoles,
  type WorkPackRow,
  type WorkPackStatus,
} from "@/api/client";

// Pack control — the NR/L2/OHS/019-shaped lifecycle:
//   draft --(PIC verifies)--> verified --(RM authorises)--> authorised --> issued
// The checks that can't be skipped: before signing, the signer must open and
// review every key section (recorded per section, per stage, per revision).
// The server computes what's required and whether the caller may sign;
// this panel renders it and records the reviews as the user opens sections.

const props = defineProps<{ pack: WorkPackRow }>();
const emit = defineEmits<{ changed: [] }>();

const queryClient = useQueryClient();
const packId = computed(() => props.pack.id);
const { data: control, refetch } = useQuery({
  queryKey: ["work-pack-control", packId],
  queryFn: () => getWorkPackControl(packId.value),
});
// Any change to the pack (documents, brief, location…) can change what's
// required or supersede the version — re-derive.
watch(
  () => [props.pack.last_updated_at, props.pack.status, props.pack.revision],
  () => void refetch(),
);

const status = computed(() => (props.pack.status as WorkPackStatus) ?? "draft");
const stage = computed(() => control.value?.stage ?? "verify");
const stageLabel = computed(() =>
  stage.value === "verify"
    ? "Verification — Person in Charge"
    : stage.value === "authorise"
      ? "Authorisation — Responsible Manager"
      : stage.value === "issue"
        ? "Issue to site"
        : "Issued",
);

const STEPS: Array<{ key: WorkPackStatus; label: string }> = [
  { key: "draft", label: "Draft" },
  { key: "verified", label: "Verified" },
  { key: "authorised", label: "Authorised" },
  { key: "issued", label: "Issued" },
];
const stepIndex = computed(() => STEPS.findIndex((s) => s.key === status.value));

function fmtDateTime(iso: string | null | undefined): string {
  return iso
    ? new Date(iso).toLocaleString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "";
}

// ---- 019 roles ----
const ROLE_FIELDS: Array<{ key: WorkPackRoleKey; label: string; hint: string }> = [
  { key: "planner", label: "Planner", hint: "Builds the pack" },
  { key: "person_in_charge", label: "Person in Charge (PIC)", hint: "Verifies it's fit to deliver" },
  { key: "responsible_manager", label: "Responsible Manager (RM)", hint: "Authorises the safe system of work" },
  { key: "coss", label: "COSS / SWL", hint: "Delivers the briefing from it" },
];
const rolesEditing = ref(false);
const rolesBusy = ref(false);
const rolesError = ref<string | null>(null);
const rolesForm = reactive<Record<WorkPackRoleKey, string>>({
  planner: "",
  person_in_charge: "",
  responsible_manager: "",
  coss: "",
});
function roleName(key: WorkPackRoleKey): string {
  return props.pack.roles?.[key]?.name ?? "";
}
function startRoles(): void {
  for (const f of ROLE_FIELDS) rolesForm[f.key] = roleName(f.key);
  rolesError.value = null;
  rolesEditing.value = true;
}
async function saveRoles(): Promise<void> {
  rolesBusy.value = true;
  rolesError.value = null;
  try {
    const roles: WorkPackRoles = {};
    for (const f of ROLE_FIELDS) {
      const name = rolesForm[f.key].trim();
      roles[f.key] = { name: name || null, person_id: props.pack.roles?.[f.key]?.person_id ?? null };
    }
    await updateWorkPackRoles(props.pack.id, roles);
    rolesEditing.value = false;
    emit("changed");
  } catch (e) {
    rolesError.value = e instanceof Error ? e.message : String(e);
  } finally {
    rolesBusy.value = false;
  }
}
const rolesSet = computed(() => ROLE_FIELDS.filter((f) => roleName(f.key)));

// ---- key-section reviews ----
const reviewBusy = ref<string | null>(null);
const reviewError = ref<string | null>(null);

// Opening a section = scroll it into view, then record the review. The
// record is what the gate checks; the scroll is so it's actually been seen.
async function openAndReview(sectionKey: string): Promise<void> {
  const el = document.getElementById(`wp-sec-${sectionKey}`);
  el?.scrollIntoView({ behavior: "smooth", block: "start" });
  el?.classList.add("wp-sec--flash");
  setTimeout(() => el?.classList.remove("wp-sec--flash"), 1600);
  if (stage.value !== "verify" && stage.value !== "authorise") return;
  reviewBusy.value = sectionKey;
  reviewError.value = null;
  try {
    await reviewWorkPackSection(props.pack.id, sectionKey, stage.value);
    await refetch();
    await queryClient.invalidateQueries({ queryKey: ["work-pack-history", packId] });
  } catch (e) {
    reviewError.value = e instanceof Error ? e.message : String(e);
  } finally {
    reviewBusy.value = null;
  }
}

const reviewedCount = computed(
  () => (control.value?.sections ?? []).filter((s) => s.reviewed_by_me).length,
);

// ---- sign-offs ----
const signing = ref(false);
const signError = ref<string | null>(null);
const note = ref("");

async function sign(kind: "verify" | "authorise" | "issue"): Promise<void> {
  signing.value = true;
  signError.value = null;
  try {
    if (kind === "verify") await verifyWorkPack(props.pack.id, note.value.trim() || undefined);
    else if (kind === "authorise") await authoriseWorkPack(props.pack.id, note.value.trim() || undefined);
    else await issueWorkPack(props.pack.id);
    note.value = "";
    emit("changed");
    await nextTick();
    await refetch();
  } catch (e) {
    signError.value = e instanceof Error ? e.message : String(e);
  } finally {
    signing.value = false;
  }
}

defineExpose({ openAndReview });
</script>

<template>
  <section
    id="wp-control"
    class="scroll-mt-4 rounded-xl border-2 border-brand-primary/30 bg-neutral-0 p-5"
  >
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 class="font-semibold">
          Pack control
          <span class="ml-1 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-bold text-neutral-500">
            Rev {{ pack.revision }}
          </span>
        </h2>
        <p class="text-sm text-neutral-400">
          Planned and verified to NR/L2/OHS/019 — the PIC verifies, the RM
          authorises, then the pack is issued to site.
        </p>
      </div>
      <span
        class="rounded-full px-3 py-1 text-xs font-semibold"
        :class="status === 'issued' || status === 'authorised'
          ? 'bg-semantic-success/10 text-semantic-success'
          : status === 'verified'
            ? 'bg-brand-primary/10 text-brand-primary'
            : 'bg-neutral-100 text-neutral-600'"
      >
        {{ WORK_PACK_STATUS_LABEL[status] ?? status }}
      </span>
    </div>

    <!-- Lifecycle stepper -->
    <ol class="mt-4 grid grid-cols-4 gap-1 text-xs">
      <li
        v-for="(s, i) in STEPS"
        :key="s.key"
        class="rounded-md border px-2 py-1.5 text-center"
        :class="i < stepIndex
          ? 'border-semantic-success/30 bg-semantic-success/10 text-semantic-success'
          : i === stepIndex
            ? 'border-brand-primary bg-brand-primary/10 font-semibold text-brand-primary'
            : 'border-neutral-200 text-neutral-400'"
      >
        {{ s.label }}
        <span
          v-if="s.key === 'verified' && pack.verified_by_name"
          class="block truncate font-normal text-neutral-500"
          :title="fmtDateTime(pack.verified_at)"
        >{{ pack.verified_by_name }}</span>
        <span
          v-if="s.key === 'authorised' && pack.authorised_by_name"
          class="block truncate font-normal text-neutral-500"
          :title="fmtDateTime(pack.authorised_at)"
        >{{ pack.authorised_by_name }}</span>
        <span
          v-if="s.key === 'issued' && pack.issued_by_name"
          class="block truncate font-normal text-neutral-500"
          :title="fmtDateTime(pack.issued_at)"
        >{{ pack.issued_by_name }}</span>
      </li>
    </ol>

    <!-- Roles -->
    <div class="mt-5 flex items-start justify-between gap-4">
      <div>
        <h3 class="text-sm font-semibold text-neutral-900">
          Roles
        </h3>
        <p
          v-if="!rolesEditing && !rolesSet.length"
          class="text-sm text-neutral-400"
        >
          Who plans, verifies, authorises and briefs this pack.
        </p>
      </div>
      <button
        v-if="!rolesEditing && status !== 'issued'"
        type="button"
        class="shrink-0 text-xs text-brand-primary hover:underline"
        @click="startRoles"
      >
        {{ rolesSet.length ? "Edit roles" : "Assign roles" }}
      </button>
    </div>
    <dl
      v-if="!rolesEditing && rolesSet.length"
      class="mt-2 grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2"
    >
      <template
        v-for="f in rolesSet"
        :key="f.key"
      >
        <div class="flex gap-2">
          <dt class="w-40 shrink-0 text-neutral-400">
            {{ f.label }}
          </dt>
          <dd class="font-medium text-neutral-900">
            {{ roleName(f.key) }}
          </dd>
        </div>
      </template>
    </dl>
    <div
      v-if="rolesEditing"
      class="mt-2 grid gap-3 rounded-lg bg-neutral-50 p-4 sm:grid-cols-2"
    >
      <label
        v-for="f in ROLE_FIELDS"
        :key="f.key"
        class="grid gap-1 text-xs text-neutral-500"
      >
        <span>{{ f.label }} <span class="text-neutral-400">· {{ f.hint }}</span></span>
        <input
          v-model="rolesForm[f.key]"
          type="text"
          class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
        >
      </label>
      <div class="flex items-center gap-3 sm:col-span-2">
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="rolesBusy"
          @click="saveRoles"
        >
          {{ rolesBusy ? "Saving…" : "Save roles" }}
        </button>
        <button
          type="button"
          class="text-sm text-neutral-500 hover:text-neutral-900"
          @click="rolesEditing = false"
        >
          Cancel
        </button>
        <span
          v-if="rolesError"
          class="text-sm text-semantic-danger"
        >{{ rolesError }}</span>
      </div>
    </div>

    <!-- Stage: the checks that can't be skipped -->
    <div
      v-if="control && (stage === 'verify' || stage === 'authorise')"
      class="mt-5"
    >
      <h3 class="text-sm font-semibold text-neutral-900">
        {{ stageLabel }}
      </h3>
      <p class="text-sm text-neutral-400">
        Open and review each key section before you sign —
        {{ reviewedCount }}/{{ control.sections.length }} reviewed by you at Rev {{ control.revision }}.
      </p>
      <ul class="mt-2 grid gap-1.5">
        <li
          v-for="s in control.sections"
          :key="s.key"
          class="flex flex-wrap items-center gap-2 text-sm"
        >
          <span
            class="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px]"
            :class="s.reviewed_by_me
              ? 'bg-semantic-success/15 text-semantic-success'
              : 'border border-neutral-300 text-neutral-300'"
          >{{ s.reviewed_by_me ? "✓" : "" }}</span>
          <span :class="s.reviewed_by_me ? 'text-neutral-700' : 'text-neutral-900'">
            {{ s.label }}
          </span>
          <button
            type="button"
            class="text-xs text-brand-primary hover:underline disabled:opacity-50"
            :disabled="reviewBusy === s.key"
            @click="openAndReview(s.key)"
          >
            {{ reviewBusy === s.key ? "Recording…" : s.reviewed_by_me ? "Open again" : "Open & review" }}
          </button>
          <span
            v-if="s.reviewed_by_name && !s.reviewed_by_me"
            class="text-xs text-neutral-400"
          >reviewed by {{ s.reviewed_by_name }}</span>
        </li>
      </ul>
      <p
        v-if="reviewError"
        class="mt-2 text-sm text-semantic-danger"
      >
        {{ reviewError }}
      </p>

      <ul
        v-if="control.missing.length && stage === 'verify'"
        class="mt-3 grid gap-1 rounded-md bg-semantic-warning/10 p-3 text-sm text-semantic-warning"
      >
        <li
          v-for="m in control.missing"
          :key="m"
        >
          Still missing: {{ m }}
        </li>
      </ul>

      <div class="mt-4 flex flex-wrap items-end gap-3">
        <label class="grid min-w-[16rem] flex-1 gap-1 text-xs text-neutral-500">
          <span>Note (optional)</span>
          <input
            v-model="note"
            type="text"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
            placeholder="e.g. Verified against WON item 4 and site walk 28 Oct"
          >
        </label>
        <button
          v-if="stage === 'verify'"
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on hover:opacity-90 disabled:opacity-50"
          :disabled="signing || !control.can_verify"
          :title="control.can_verify ? '' : control.blockers.join('\n')"
          @click="sign('verify')"
        >
          {{ signing ? "Signing…" : "Verify pack (PIC)" }}
        </button>
        <button
          v-else
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on hover:opacity-90 disabled:opacity-50"
          :disabled="signing || !control.can_authorise"
          :title="control.can_authorise ? '' : control.blockers.join('\n')"
          @click="sign('authorise')"
        >
          {{ signing ? "Signing…" : "Authorise pack (RM)" }}
        </button>
      </div>
      <p
        v-if="stage === 'authorise'"
        class="mt-2 text-xs text-neutral-400"
      >
        Authorising needs a management role — the Responsible Manager is
        accountable for the safe system of work.
      </p>
    </div>

    <!-- Stage: issue -->
    <div
      v-else-if="control && stage === 'issue'"
      class="mt-5 flex flex-wrap items-center justify-between gap-3"
    >
      <p class="text-sm text-neutral-700">
        Verified and authorised at Rev {{ control.revision }}. Issue it to site —
        the gang receives it on the app and the COSS briefs from it.
      </p>
      <button
        type="button"
        class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on hover:opacity-90 disabled:opacity-50"
        :disabled="signing || !control.can_issue"
        @click="sign('issue')"
      >
        {{ signing ? "Issuing…" : "Issue to site" }}
      </button>
    </div>
    <p
      v-else-if="status === 'issued'"
      class="mt-5 text-sm text-neutral-700"
    >
      Issued at Rev {{ pack.revision }} · verified by {{ pack.verified_by_name ?? "—" }}
      · authorised by {{ pack.authorised_by_name ?? "—" }}.
    </p>

    <p
      v-if="signError"
      class="mt-2 text-sm text-semantic-danger"
    >
      {{ signError }}
    </p>
    <p
      v-if="status === 'verified' || status === 'authorised'"
      class="mt-3 text-xs text-neutral-400"
    >
      One live version: any change to the pack's content from here supersedes
      Rev {{ pack.revision }} — it returns to draft and is re-verified.
    </p>
  </section>
</template>

<style>
.wp-sec--flash {
  animation: wp-flash 1.6s ease-out;
}
@keyframes wp-flash {
  0% { box-shadow: 0 0 0 4px rgba(173, 45, 29, 0.35); }
  100% { box-shadow: 0 0 0 0 rgba(173, 45, 29, 0); }
}
</style>
